import * as THREE from 'three'

import { Item } from '../api/items'
import { Job } from '../api/jobs'

import { FloorProgressTransform, worldToPlan } from './progressFloorTransform'
import {
  colorForItemJobs,
  getTopVisibleJobForItem,
  PROGRESS_ITEM_GREY,
} from './progressItemDisplay'
import { resolveProgressStlUrl } from './progressStlUrl'

/** Normalized STL identity (presigned query strings ignored). */
export function stlUrlCacheKey(url: string): string {
  try {
    const u = new URL(url, window.location.origin)
    return `${u.origin}${u.pathname}`
  } catch {
    return url
  }
}

/**
 * One solid color for an entire STL: if any linked item has visible 2D progress,
 * use the top display_order color among those segments; otherwise grey.
 */
export function getAggregateStlMeshColor(
  jobs: Job[],
  selectedJobTypeIds: number[],
  stlUrl: string,
): string {
  const jobsForTypes = selectedJobTypeIds.length
    ? jobs.filter(j => selectedJobTypeIds.includes(j.type.job_type.id))
    : []

  const urlKey = stlUrlCacheKey(stlUrl)
  const byItemId = new Map<number, Job[]>()

  for (const job of jobsForTypes) {
    const itemUrl = resolveProgressStlUrl(job.item)
    if (!itemUrl || stlUrlCacheKey(itemUrl) !== urlKey) {
      continue
    }
    const list = byItemId.get(job.item.id)
    if (list) {
      list.push(job)
    } else {
      byItemId.set(job.item.id, [job])
    }
  }

  let bestColor: string | null = null
  let bestDisplayOrder = Number.MIN_SAFE_INTEGER
  let anyHasProgress = false

  byItemId.forEach(group => {
    const topJob = getTopVisibleJobForItem(group)
    if (!topJob) {
      return
    }
    anyHasProgress = true
    const order = topJob.type.job_type.display_order
    const color = colorForItemJobs(group)
    if (order >= bestDisplayOrder) {
      bestDisplayOrder = order
      bestColor = color
    }
  })

  if (!anyHasProgress) {
    return PROGRESS_ITEM_GREY
  }
  return bestColor ?? PROGRESS_ITEM_GREY
}

/** Matches LayerManager line hit-test padding. */
const LINE_HIT_TEST_HALF_WIDTH = 12
const POINT_HIT_RADIUS = 22

export type ItemPlanLayer = {
  itemId: number
  displayShape: string
  planPoints: ReadonlyArray<readonly [number, number]>
  color: string
  /** Top visible 2D layer order at this observation (higher = wins overlaps). */
  displayOrder: number
  minX: number
  minY: number
  maxX: number
  maxY: number
}

function planBbox(
  points: ReadonlyArray<readonly [number, number]>,
  pad: number,
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (!points.length) {
    return null
  }
  let minX = points[0][0]
  let minY = points[0][1]
  let maxX = points[0][0]
  let maxY = points[0][1]
  for (let i = 1; i < points.length; i++) {
    minX = Math.min(minX, points[i][0])
    minY = Math.min(minY, points[i][1])
    maxX = Math.max(maxX, points[i][0])
    maxY = Math.max(maxY, points[i][1])
  }
  return {
    minX: minX - pad,
    minY: minY - pad,
    maxX: maxX + pad,
    maxY: maxY + pad,
  }
}

function distSqToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1
  const dy = y2 - y1
  const lenSq = dx * dx + dy * dy
  if (lenSq < 1e-12) {
    const ox = px - x1
    const oy = py - y1
    return ox * ox + oy * oy
  }
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const cx = x1 + t * dx
  const cy = y1 + t * dy
  const ox = px - cx
  const oy = py - cy
  return ox * ox + oy * oy
}

function pointInPolygon(
  px: number,
  py: number,
  pts: ReadonlyArray<readonly [number, number]>,
): boolean {
  if (pts.length < 3) {
    return false
  }
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i][0]
    const yi = pts[i][1]
    const xj = pts[j][0]
    const yj = pts[j][1]
    const intersect =
      yi > py !== yj > py &&
      px < ((xj - xi) * (py - yi)) / (yj - yi + 1e-12) + xi
    if (intersect) {
      inside = !inside
    }
  }
  return inside
}

function hitTestLayer(px: number, py: number, layer: ItemPlanLayer): boolean {
  const pts = layer.planPoints
  if (!pts.length) {
    return false
  }

  if (layer.displayShape === 'point') {
    const r2 = POINT_HIT_RADIUS * POINT_HIT_RADIUS
    return (
      distSqToSegment(px, py, pts[0][0], pts[0][1], pts[0][0], pts[0][1]) <= r2
    )
  }

  if (layer.displayShape === 'line' || pts.length === 2) {
    const thresh = LINE_HIT_TEST_HALF_WIDTH * LINE_HIT_TEST_HALF_WIDTH
    for (let i = 0; i < pts.length - 1; i++) {
      if (
        distSqToSegment(
          px,
          py,
          pts[i][0],
          pts[i][1],
          pts[i + 1][0],
          pts[i + 1][1],
        ) <= thresh
      ) {
        return true
      }
    }
    return false
  }

  return pointInPolygon(px, py, pts)
}

const PLAN_CELL_SIZE = 180

type PlanSpatialIndex = {
  cellSize: number
  cells: Map<string, number[]>
}

function cellKey(cx: number, cy: number): string {
  return `${cx},${cy}`
}

function buildPlanSpatialIndex(layers: ItemPlanLayer[]): PlanSpatialIndex {
  const cells = new Map<string, number[]>()
  const cellSize = PLAN_CELL_SIZE

  layers.forEach((layer, index) => {
    const pad =
      layer.displayShape === 'line'
        ? LINE_HIT_TEST_HALF_WIDTH
        : layer.displayShape === 'point'
          ? POINT_HIT_RADIUS
          : 0
    const minCx = Math.floor((layer.minX - pad) / cellSize)
    const maxCx = Math.floor((layer.maxX + pad) / cellSize)
    const minCy = Math.floor((layer.minY - pad) / cellSize)
    const maxCy = Math.floor((layer.maxY + pad) / cellSize)
    for (let cx = minCx; cx <= maxCx; cx += 1) {
      for (let cy = minCy; cy <= maxCy; cy += 1) {
        const key = cellKey(cx, cy)
        const bucket = cells.get(key)
        if (bucket) {
          bucket.push(index)
        } else {
          cells.set(key, [index])
        }
      }
    }
  })

  return { cellSize, cells }
}

function findOwningItemLayer(
  px: number,
  py: number,
  layers: ItemPlanLayer[],
  index: PlanSpatialIndex,
): ItemPlanLayer | null {
  const cx = Math.floor(px / index.cellSize)
  const cy = Math.floor(py / index.cellSize)
  let best: ItemPlanLayer | null = null

  for (let dx = -1; dx <= 1; dx += 1) {
    for (let dy = -1; dy <= 1; dy += 1) {
      const bucket = index.cells.get(cellKey(cx + dx, cy + dy))
      if (!bucket) {
        continue
      }
      for (const layerIndex of bucket) {
        const layer = layers[layerIndex]
        if (
          px < layer.minX ||
          px > layer.maxX ||
          py < layer.minY ||
          py > layer.maxY
        ) {
          continue
        }
        if (!hitTestLayer(px, py, layer)) {
          continue
        }
        if (!best || layer.displayOrder > best.displayOrder) {
          best = layer
        }
      }
    }
  }

  return best
}

export type PlanHitTester = {
  hitTest: (planX: number, planY: number) => Item | null
  hitTestRect: (
    minX: number,
    minY: number,
    maxX: number,
    maxY: number,
  ) => Item[]
}

function normalizePlanRect(
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
) {
  return {
    left: Math.min(minX, maxX),
    top: Math.min(minY, maxY),
    right: Math.max(minX, maxX),
    bottom: Math.max(minY, maxY),
  }
}

function layerIntersectsPlanRect(
  layer: ItemPlanLayer,
  left: number,
  top: number,
  right: number,
  bottom: number,
): boolean {
  if (
    layer.maxX < left ||
    layer.minX > right ||
    layer.maxY < top ||
    layer.minY > bottom
  ) {
    return false
  }

  if (layer.displayShape === 'point') {
    const [px, py] = layer.planPoints[0] ?? []
    if (px == null || py == null) {
      return false
    }
    return px >= left && px <= right && py >= top && py <= bottom
  }

  // Line / area: also accept any vertex in rect.
  for (const [px, py] of layer.planPoints) {
    if (px >= left && px <= right && py >= top && py <= bottom) {
      return true
    }
  }
  if (layer.displayShape === 'area') {
    const cx = (left + right) / 2
    const cy = (top + bottom) / 2
    if (pointInPolygon(cx, cy, layer.planPoints)) {
      return true
    }
  }
  return layer.displayShape === 'line' || layer.displayShape === 'area'
}

/** Spatial-index hit test for 2D plan overlays (replaces retained Path2D objects). */
export function createPlanHitTester(
  jobs: Job[],
  selectedJobTypeIds: number[],
): PlanHitTester {
  const layers = buildItemPlanLayers(jobs, selectedJobTypeIds)
  const index = buildPlanSpatialIndex(layers)
  const itemById = new Map<number, Item>()
  for (const job of jobs) {
    if (!itemById.has(job.item.id)) {
      itemById.set(job.item.id, job.item)
    }
  }

  return {
    hitTest(planX: number, planY: number) {
      const layer = findOwningItemLayer(planX, planY, layers, index)
      if (!layer) {
        return null
      }
      return itemById.get(layer.itemId) ?? null
    },
    hitTestRect(minX, minY, maxX, maxY) {
      const { left, top, right, bottom } = normalizePlanRect(
        minX,
        minY,
        maxX,
        maxY,
      )
      if (right - left < 1 && bottom - top < 1) {
        return []
      }
      const matched = new Map<number, Item>()
      for (const layer of layers) {
        if (!layerIntersectsPlanRect(layer, left, top, right, bottom)) {
          continue
        }
        const item = itemById.get(layer.itemId)
        if (item) {
          matched.set(item.id, item)
        }
      }
      return Array.from(matched.values())
    },
  }
}

/**
 * 2D plan layers for selected job types — colors match progressItemDisplay / LayerManager.
 */
export function buildItemPlanLayers(
  jobs: Job[],
  selectedJobTypeIds: number[],
): ItemPlanLayer[] {
  const jobsForTypes = selectedJobTypeIds.length
    ? jobs.filter(j => selectedJobTypeIds.includes(j.type.job_type.id))
    : []

  const byItemId = new Map<number, Job[]>()
  for (const job of jobsForTypes) {
    const list = byItemId.get(job.item.id)
    if (list) {
      list.push(job)
    } else {
      byItemId.set(job.item.id, [job])
    }
  }

  const layers: ItemPlanLayer[] = []

  byItemId.forEach((group, itemId) => {
    const representative = group[0]
    const position = representative.item.position
    if (!position?.length) {
      return
    }

    const planPoints = position.map(p => [Number(p.x), Number(p.y)] as const)
    const topJob = getTopVisibleJobForItem(group)
    const displayShape =
      topJob?.type.job_type.display_shape ??
      representative.type.job_type.display_shape
    const pad =
      displayShape === 'line'
        ? LINE_HIT_TEST_HALF_WIDTH
        : displayShape === 'point'
          ? POINT_HIT_RADIUS
          : 0
    const bbox = planBbox(planPoints, pad)
    if (!bbox) {
      return
    }

    layers.push({
      itemId,
      displayShape,
      planPoints,
      color: colorForItemJobs(group),
      displayOrder:
        topJob?.type.job_type.display_order ?? Number.MIN_SAFE_INTEGER,
      ...bbox,
    })
  })

  return layers
}

function linearColorFromHex(hex: string): THREE.Color {
  const c = new THREE.Color(hex)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- three@0.131
  ;(c as any).convertSRGBToLinear?.()
  return c
}

/**
 * Paint STL vertices from 2D plan ownership (world XY → plan hit-test → item color).
 */
export function applyVertexColorsFromPlanLayers(
  geometry: THREE.BufferGeometry,
  affine: FloorProgressTransform,
  layers: ItemPlanLayer[],
): boolean {
  if (!layers.length) {
    return false
  }

  const posAttr = geometry.getAttribute('position') as
    | THREE.BufferAttribute
    | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- three@0.131 BufferAttribute
  const posArray = (posAttr as any)?.array as Float32Array | undefined
  if (!posArray?.length) {
    return false
  }

  const index = buildPlanSpatialIndex(layers)
  const defaultLinear = linearColorFromHex(PROGRESS_ITEM_GREY)
  const count = posArray.length / 3
  const colors = new Float32Array(count * 3)

  for (let i = 0; i < count; i += 1) {
    const wx = posArray[i * 3]
    const wy = posArray[i * 3 + 1]
    const [px, py] = worldToPlan(affine, wx, wy)
    const owner = findOwningItemLayer(px, py, layers, index)
    const linear = owner ? linearColorFromHex(owner.color) : defaultLinear
    colors[i * 3] = linear.r
    colors[i * 3 + 1] = linear.g
    colors[i * 3 + 2] = linear.b
  }

  const geometryWithColors = geometry as THREE.BufferGeometry & {
    setAttribute(name: string, attribute: THREE.BufferAttribute): void
  }
  geometryWithColors.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  return true
}
